import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { VIEWER_MOBILE_MEDIA_QUERY } from "../viewerBreakpoint";
import styles from "./Drawer.module.css";

/** Gap between the map-scale HUD chip bottom edge and the drawer sheet top. */
const DRAWER_SCALE_CHIP_GAP_PX = 16;

/**
 * Sliding-window length (ms) used to compute release velocity. Long enough to
 * smooth out a single jittery pointer sample, short enough that a slow tail
 * after a fast pull still reads as "user lifted off without inertia".
 */
const VELOCITY_WINDOW_MS = 80;

/**
 * Inertial projection horizon (ms) used for the dismiss decision. A release
 * with downward velocity `v` is treated as if the sheet would coast another
 * `v * PROJECTION_HORIZON_MS` pixels before settling.
 */
const PROJECTION_HORIZON_MS = 100;

/**
 * Raw downward speed (px/ms) that always dismisses regardless of drag offset.
 * iOS sheets feel snappy because a quick flick from near the top still closes
 * — this catches that case independently of {@link PROJECTION_HORIZON_MS}.
 */
const FLICK_DISMISS_PX_PER_MS = 0.6;

/** Resistance factor applied to upward pull (negative drag) for rubber-band. */
const UPWARD_RUBBER_BAND_RATIO = 0.35;
/** Hard ceiling on rubber-band travel (negative direction, in px). */
const UPWARD_RUBBER_BAND_MAX_PX = 24;

function computeRecentVelocity(
  samples: ReadonlyArray<{ y: number; t: number }>
): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1]!;
  const cutoff = last.t - VELOCITY_WINDOW_MS;
  let oldest = samples[0]!;
  for (const sample of samples) {
    if (sample.t >= cutoff) {
      oldest = sample;
      break;
    }
  }
  const dt = Math.max(1, last.t - oldest.t);
  return (last.y - oldest.y) / dt;
}

function resolveSheetMaxHeightPx(
  viewportH: number,
  sheet: HTMLElement,
  aboveBottomNav: boolean
): number {
  const fallback = Math.min(viewportH * 0.88, 640);

  if (
    aboveBottomNav &&
    typeof window !== "undefined" &&
    window.matchMedia(VIEWER_MOBILE_MEDIA_QUERY).matches
  ) {
    const scaleMount = document.querySelector(".hud-bottom-map-scale-slot");
    if (scaleMount) {
      const { bottom } = scaleMount.getBoundingClientRect();
      if (bottom > 0) {
        return Math.max(
          0,
          Math.floor(viewportH - bottom - DRAWER_SCALE_CHIP_GAP_PX)
        );
      }
    }
  }

  const maxHeight = getComputedStyle(sheet).maxHeight;
  if (maxHeight && maxHeight !== "none") {
    const parsed = parseFloat(maxHeight);
    if (Number.isFinite(parsed)) return parsed;
  }

  return fallback;
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Extra bottom padding keeps inner content clear of overlapping bottom nav
   * chrome. Ignored when {@link DrawerProps.aboveBottomNav} is true (the
   * sheet covers the bottom nav so no clearance is needed).
   */
  bottomChromeInsetPx: number;
  /**
   * When true, the sheet + scrim render above `.viewer-bottom-nav` (z 12).
   * The drawer's own header carries the close affordance instead of the
   * bottom-nav ellipsis swap. Defaults to false (legacy under-nav layout).
   */
  aboveBottomNav?: boolean;
  titleId?: string;
  /** Assistive-tech-only sheet title (`aria-labelledby`). Defaults to "Map panels". */
  sheetHeading?: string;
  /**
   * Extra class applied to BOTH the scrim overlay and the sheet. Lets
   * non-viewer hosts (e.g. the marketing nav drawer) re-scope z-index.
   */
  className?: string;
  /**
   * Action row rendered between the drag handle and the {@link controls} slot
   * — typically Close/Back on the left and Apply on the right.
   */
  topActions?: ReactNode;
  /** Segmented control or other chrome under the grab handle */
  controls?: ReactNode;
  /** Scrollable pane */
  children: ReactNode;
}

/**
 * Full-width bottom drawer: slides above the terrain, tucked under `.viewer-bottom-nav`.
 */
export function Drawer({
  open,
  onClose,
  bottomChromeInsetPx,
  aboveBottomNav = false,
  titleId,
  sheetHeading = "Map panels",
  className,
  topActions,
  controls,
  children
}: DrawerProps): ReactElement {
  const fallbackTitleId = useId();
  const headingId = titleId ?? fallbackTitleId;
  const sheetRef = useRef<HTMLElement | null>(null);
  const handleHitRef = useRef<HTMLButtonElement | null>(null);
  const headerMeasureRef = useRef<HTMLDivElement | null>(null);
  const bodyMeasureRef = useRef<HTMLDivElement | null>(null);
  /** Pointer id currently driving a handle drag (-1 when none). */
  const draggingPointerIdRef = useRef<number | null>(null);
  const dragStartClientYRef = useRef<number>(0);
  const dragStartOffsetPxRef = useRef<number>(0);
  /** Mirrors {@link dragYOffsetPx} inside pointer handlers (avoids stale closure on `pointerup`). */
  const dragYOffsetLiveRef = useRef<number>(0);
  /**
   * Recent pointer samples (`{ y, t }`) for sliding-window velocity. Single
   * sample = no velocity. Window trims to ~80ms ago so a brisk flick at the
   * very end registers even if the user paused mid-drag.
   */
  const velSamplesRef = useRef<Array<{ y: number; t: number }>>([]);
  /** Mirrors {@link dragging} for handlers / layout effects. */
  const isDraggingRef = useRef<boolean>(false);
  const [bodyHeightPx, setBodyHeightPx] = useState<number | undefined>(
    undefined
  );
  /** Vertical pull when open — combined with `--drawer-drag-y` + `.sheetOpen` */
  const [dragYOffsetPx, setDragYOffsetPx] = useState(0);
  /**
   * Mirrors {@link isDraggingRef} as React state so the sheet can pick up the
   * `.sheetDragging` class (transition: none) without re-rendering on every
   * pointer-move. Only flips on pointerdown/up/cancel.
   */
  const [dragging, setDragging] = useState(false);

  const recalcBodyHeight = useCallback((): void => {
    const sheet = sheetRef.current;
    const headerEl = headerMeasureRef.current;
    const innerEl = bodyMeasureRef.current;
    if (!sheet || !headerEl || !innerEl) return;

    const viewportH =
      typeof window !== "undefined"
        ? (window.visualViewport?.height ?? window.innerHeight)
        : 640;
    const sheetMaxPx = resolveSheetMaxHeightPx(viewportH, sheet, aboveBottomNav);
    const cs = getComputedStyle(sheet);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padBottom = parseFloat(cs.paddingBottom) || 0;
    const borderTop = parseFloat(cs.borderTopWidth) || 0;
    const maxBodyPx = Math.ceil(
      Math.max(
        0,
        sheetMaxPx - padTop - padBottom - borderTop - headerEl.offsetHeight
      )
    );
    const natural = innerEl.scrollHeight;
    const next = Math.ceil(Math.min(natural, maxBodyPx));
    setBodyHeightPx(next);
  }, [aboveBottomNav]);

  /**
   * Resync the body's pixel height after EVERY commit while open. Running this
   * on every render (rather than just `[open, bottomChromeInsetPx]`) is what
   * keeps row-to-row content swaps smoothly animated: when `children` change,
   * we re-measure the new content and `setBodyHeightPx` synchronously, so
   * React commits the new inline height in the same paint that mounts the
   * new children. Without this, the body keeps its old pixel height for one
   * paint (clipping/scrolling the new content) and the CSS transition begins
   * from that visibly-wrong intermediate frame — perceived as the drawer
   * "snapping smaller, then growing" when navigating to a taller panel. The
   * second pass after `setBodyHeightPx` no-ops via React's state bailout.
   */
  useLayoutEffect(() => {
    if (!open) return;
    // Skip recalc while the user is actively dragging — body content isn't
    // changing, and `getComputedStyle` inside recalc forces a sync layout
    // that visibly judders the drag at 60–120 Hz pointer updates.
    if (isDraggingRef.current) return;
    recalcBodyHeight();
  });

  useEffect(() => {
    if (!open) return;
    if (typeof ResizeObserver === "undefined") return;

    const innerEl = bodyMeasureRef.current;
    if (!innerEl) return;

    const ro = new ResizeObserver(() => recalcBodyHeight());
    ro.observe(innerEl);

    const scaleMount =
      aboveBottomNav &&
      typeof document !== "undefined" &&
      window.matchMedia(VIEWER_MOBILE_MEDIA_QUERY).matches
        ? document.querySelector(".hud-bottom-map-scale-slot")
        : null;
    if (scaleMount) {
      ro.observe(scaleMount);
    }

    const onVp = (): void => {
      recalcBodyHeight();
    };
    window.addEventListener("resize", onVp);
    window.visualViewport?.addEventListener("resize", onVp);

    let mo: MutationObserver | null = null;
    if (aboveBottomNav && typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => recalcBodyHeight());
      mo.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"]
      });
    }

    return () => {
      ro.disconnect();
      mo?.disconnect();
      window.removeEventListener("resize", onVp);
      window.visualViewport?.removeEventListener("resize", onVp);
    };
  }, [open, recalcBodyHeight, aboveBottomNav]);

  useEffect(() => {
    if (!open) setBodyHeightPx(undefined);
  }, [open]);

  useEffect(() => {
    if (!open) {
      draggingPointerIdRef.current = null;
      isDraggingRef.current = false;
      setDragging(false);
      velSamplesRef.current = [];
      dragYOffsetLiveRef.current = 0;
      setDragYOffsetPx(0);
    }
  }, [open]);

  useEffect(() => {
    dragYOffsetLiveRef.current = dragYOffsetPx;
  }, [dragYOffsetPx]);

  const endDragForPointerIfActive = useCallback(
    (ev: Pick<ReactPointerEvent<HTMLButtonElement>, "pointerId">): void => {
      if (draggingPointerIdRef.current !== ev.pointerId) return;
      draggingPointerIdRef.current = null;
      const hit = handleHitRef.current;
      if (hit) {
        try {
          hit.releasePointerCapture(ev.pointerId);
        } catch {
          /* not capturing */
        }
      }
    },
    []
  );

  const dismissFromDragGesture = useCallback((): void => {
    draggingPointerIdRef.current = null;
    onClose();
  }, [onClose]);

  const handleHandlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>): void => {
      if (!open || (e.pointerType === "mouse" && e.buttons !== 1)) return;
      draggingPointerIdRef.current = e.pointerId;
      dragStartClientYRef.current = e.clientY;
      dragStartOffsetPxRef.current = dragYOffsetLiveRef.current;
      velSamplesRef.current = [{ y: e.clientY, t: e.timeStamp }];
      isDraggingRef.current = true;
      setDragging(true);
      const hit = handleHitRef.current;
      if (!hit) return;
      try {
        hit.setPointerCapture(e.pointerId);
      } catch {
        /* ignore capture failures */
      }
    },
    [open]
  );

  const handleHandlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>): void => {
      if (draggingPointerIdRef.current !== e.pointerId || !open) return;
      const rawDy = e.clientY - dragStartClientYRef.current;
      // Rubber-band on upward pull: divide by ratio so 100px of finger ≈ 35px
      // of sheet movement, capped at UPWARD_RUBBER_BAND_MAX_PX. iOS does this
      // to make "you can't pull this further" feel elastic instead of locked.
      const adjDy =
        rawDy < 0
          ? Math.max(-UPWARD_RUBBER_BAND_MAX_PX, rawDy * UPWARD_RUBBER_BAND_RATIO)
          : rawDy;
      const next = Math.max(
        -UPWARD_RUBBER_BAND_MAX_PX,
        dragStartOffsetPxRef.current + adjDy
      );
      dragYOffsetLiveRef.current = next;
      setDragYOffsetPx(next);

      // Maintain a sliding window of recent samples for release velocity.
      const samples = velSamplesRef.current;
      samples.push({ y: e.clientY, t: e.timeStamp });
      const cutoff = e.timeStamp - VELOCITY_WINDOW_MS;
      while (samples.length > 2 && samples[0]!.t < cutoff) {
        samples.shift();
      }
    },
    [open]
  );

  const handleHandlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>): void => {
      if (draggingPointerIdRef.current !== e.pointerId) return;

      const offset = Math.max(0, dragYOffsetLiveRef.current);
      const velocity = computeRecentVelocity(velSamplesRef.current);
      const sheetHeight = sheetRef.current?.offsetHeight ?? 600;

      // Dismiss when the projected resting position (current offset + a short
      // inertial coast at release velocity) crosses ~25% of the sheet height,
      // OR when the user flicks fast enough on its own.
      const projected = offset + velocity * PROJECTION_HORIZON_MS;
      const distanceThreshold = Math.max(120, sheetHeight * 0.25);
      const shouldDismiss =
        projected >= distanceThreshold || velocity >= FLICK_DISMISS_PX_PER_MS;

      endDragForPointerIfActive(e);
      isDraggingRef.current = false;
      setDragging(false);
      velSamplesRef.current = [];

      if (!open) return;
      if (shouldDismiss) {
        // Leave dragYOffsetPx alone — onClose flips `open` to false, the
        // `.sheetOpen` class drops off, and the CSS `transform` transition
        // animates from the current dragged-px value to the closed 104%.
        // The close-side useEffect resets the offset state once flushed.
        dismissFromDragGesture();
      } else {
        // Snap back to rest. The `.sheetDragging` class is gone so the CSS
        // transition handles the spring back smoothly.
        dragYOffsetLiveRef.current = 0;
        setDragYOffsetPx(0);
      }
    },
    [dismissFromDragGesture, endDragForPointerIfActive, open]
  );

  const handleHandlePointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>): void => {
      if (draggingPointerIdRef.current !== e.pointerId) return;
      endDragForPointerIfActive(e);
      isDraggingRef.current = false;
      setDragging(false);
      velSamplesRef.current = [];
      if (open) {
        dragYOffsetLiveRef.current = 0;
        setDragYOffsetPx(0);
      }
    },
    [endDragForPointerIfActive, open]
  );

  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const bodyStyle: CSSProperties | undefined =
    open && typeof bodyHeightPx === "number"
      ? { height: `${bodyHeightPx}px` }
      : undefined;

  const effectiveBottomInsetPx = aboveBottomNav ? 0 : bottomChromeInsetPx;
  const sheetStyle: CSSProperties & { "--drawer-drag-y"?: string } = {
    paddingBottom: `calc(${effectiveBottomInsetPx}px + env(safe-area-inset-bottom, 0px) + 14px)`,
    // Apply offset for any non-zero value so rubber-band on upward pull (which
    // produces a small negative offset) is visible too.
    ...(open && dragYOffsetPx !== 0
      ? { "--drawer-drag-y": `${dragYOffsetPx}px` }
      : {})
  };

  const node = (
    <>
      <button
        type="button"
        aria-label="Close drawer"
        className={`viewer-drawer-chrome ${styles.overlay}${open ? ` ${styles.overlayVisible}` : ""}${aboveBottomNav ? ` ${styles.overlayAboveBottomNav}` : ""}${className ? ` ${className}` : ""}`}
        onClick={onClose}
      />
      <section
        ref={sheetRef}
        className={`viewer-drawer-chrome ${styles.sheet}${open ? ` ${styles.sheetOpen}` : ""}${aboveBottomNav ? ` ${styles.sheetAboveBottomNav}` : ""}${dragging ? ` ${styles.sheetDragging}` : ""}${className ? ` ${className}` : ""}`}
        style={sheetStyle}
        aria-hidden={!open}
        aria-modal={open ? true : undefined}
        role="dialog"
        aria-labelledby={headingId}
      >
        <h2 id={headingId} className={styles.visuallyHidden}>
          {sheetHeading}
        </h2>
        <div ref={headerMeasureRef} className={styles.sheetHeader}>
          <button
            type="button"
            ref={handleHitRef}
            className={styles.handleHit}
            onPointerDown={handleHandlePointerDown}
            onPointerMove={handleHandlePointerMove}
            onPointerUp={handleHandlePointerUp}
            onPointerCancel={handleHandlePointerCancel}
            aria-label="Drag handle: pull down to close"
          >
            <span className={styles.handle} aria-hidden />
          </button>
          {topActions ? (
            <div className={styles.topActions}>{topActions}</div>
          ) : null}
          {controls ? (
            <div className={styles.controlsSlot}>{controls}</div>
          ) : null}
        </div>
        <div className={styles.body} style={bodyStyle}>
          <div ref={bodyMeasureRef} className={styles.bodyInner}>
            {children}
          </div>
        </div>
      </section>
    </>
  );

  if (typeof document === "undefined") return <></>;

  return createPortal(node, document.body);
}
